import { IMatrix4, IVector2, IVector3, IViewer } from '@inter-ikea-betrakta/viewer';
import { OrthographicFixedCamera } from '@inter-ikea-betrakta/viewer/dist/modules/cameras/OrthographicFixedCamera';

/**
 * Initializes the Big Screen Planner with the given viewer and dimensions. Note that it is the canvas size and not the actual screen size that is desired.
 *
 * @param viewer - The viewer instance where the scene will be rendered.
 * @param camvasWidth - The width of the canvas in pixels.
 * @param canvasHeight - The height of the canvas in pixels.
 *
 * @remarks
 * This function sets up the scene parameters including the floor height, target center, and viewing direction.
 * It then creates a camera configured for the big screen and pushes it to the viewer.
 */
export const initBigScreenPlanner = (
  viewer: IViewer,
  canvasWidth: number,
  canvasHeight: number
): void => {
  // The height of the bottom of the screen above the physical floor
  const screenBottomToFloorHeight = 0.0;

  // ======= Scene parameters =======
  // The y-value of the floor in the scene
  const screenFloorHeight = 0.0;

  // The center of the solution in the scene. y-value can be ignored
  const target_center = new IVector3(0, 0, 0);

  // Which way the camera should look in the solution.
  // Should be 0 in the y-value.
  const viewing_direction = new IVector3(0, 0, -1);

  // Result
  const camera = create_bigscreen_camera(
    viewer,
    target_center,
    screenFloorHeight + screenBottomToFloorHeight,
    canvasWidth,
    canvasHeight,
    viewing_direction
  );
  viewer.push_camera(camera);
};

/**
 * Creates an OrthographicFixedCamera meant to be used for big-screen visualization.
 * @param viewer The viewer object
 * @param target_center The center position of the solution. y-value can be ignored
 * @param floor_offset The sum of the height of the floor in the scene, and the height of the bottom of the physical screen from the physical floor
 * @param screen_width_meters How wide the physical screen is in meters
 * @param screen_height_meters How tall the physical screen is in meters
 * @param viewing_direction The direction the camera should look. Must be normalized and should have no vertical (y) component.
 */
const create_bigscreen_camera = (
  viewer: IViewer,
  target_center: IVector3,
  floor_offset: number,
  screen_width_meters: number,
  screen_height_meters: number,
  viewing_direction: IVector3
): OrthographicFixedCamera => {
  const camera = new OrthographicFixedCamera(new IVector2(0, 0), viewer);
  camera.update_aspect_ratio(screen_width_meters / screen_height_meters);
  const look_at_matrix = IMatrix4.look_at(
    new IVector3(0, 0, 0),
    viewing_direction,
    new IVector3(0, 1, 0)
  );
  camera.set_view_plane(look_at_matrix[0], look_at_matrix[1]);
  camera.position_on_plane = new IVector2(
    IVector3.dot(look_at_matrix[0], target_center),
    0.5 * (screen_height_meters + floor_offset)
  );
  camera.size = screen_height_meters;

  return camera;
};
